import React, { useCallback } from 'react';

import { Theme, createStyles, WithStyles, withStyles } from '@material-ui/core/styles';
import {
    Typography,
    DialogTitle as MuiDialogTitle,
    DialogContent as MuiDialogContent,
    DialogActions as MuiDialogActions,
    Dialog as MuiDialog,
} from '@material-ui/core';
import { AlmeAppState } from '../AlmeApp/AlmeAppState';

import dialogStyles from './AlmeDialog.styles';
import { FocusComponentProps } from '../../models/ComponentProps';
import { AlmeDialogConfig } from './AlmeDialogConfig';
import { AlmeCloseIcon } from '../AlmeCloseIcon/AlmeCloseIcon';
import { useFocusStyles } from '../FocusVisibleStyle';

const styles = (theme: Theme) => createStyles({
    root: {
        margin: 0,
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
    },
    closeButtonTopRight: {
        fontWeight: theme.typography.fontWeightBold,
        fontSize: theme.typography.h6.fontSize,
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
    },
    closeButton: {
        fontWeight: theme.typography.fontWeightBold,
        fontSize: theme.typography.h6.fontSize,
    },
});

export interface DialogTitleProps extends WithStyles<typeof styles> {
    children: React.ReactNode;
    onClose: () => void;
    focusProps?: FocusComponentProps;
    almeConfig: AlmeDialogConfig;
    id: string;
}

export const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, almeConfig, ...other } = props;
    const Focusclasses = useFocusStyles();
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography variant="h6">{children}</Typography>
            <AlmeCloseIcon
                className={`${props.classes.closeButtonTopRight} ${Focusclasses.visibleFocus}`}
                almeConfig={props.almeConfig.dialog}
                id="alme-close-dialog-cancel"
                onClose={onClose}
                focusProps={props.focusProps}
            />
        </MuiDialogTitle>
    );
});

export const DialogContent = withStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
    },
}))(MuiDialogContent);

export const DialogActions = withStyles((theme: Theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(1),
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
    },
}))(MuiDialogActions);

export interface DialogProps extends WithStyles<typeof dialogStyles> {
    children: React.ReactNode;
    open: boolean;
    setIsDialogOpen: (isOpen: boolean) => void;
    id: string;
    disableEnforceFocus?: boolean;
    onClose: () => void;
    fullWidth?: boolean;
}

export const Dialog = withStyles(
    (theme) => ({
        ...dialogStyles(theme),
    }),
    { withTheme: true },
)((props: DialogProps) => {
    const { children, classes, setIsDialogOpen, ...other } = props;

    const setOpen = () => {
        setIsDialogOpen(true);
    };

    const setClose = () => {
        setIsDialogOpen(false);
    };

    return (
        <MuiDialog
            {...other}
            onEntered={setOpen}
            onExited={setClose}
            className={`${props.classes.dialogPaper}`}
            container={() => document.getElementById("nit-alme-window-root")}
        > {children}</MuiDialog >);
});

export function SetIsDialogOpen(almeState: AlmeAppState, setAlmeState: (state: AlmeAppState) => void): (isOpen: boolean) => void {
    return useCallback((isOpen: boolean) => {
        setAlmeState({
            ...almeState,
            isDialogOpen: isOpen,
        });
    }, [almeState, setAlmeState]);
}
